import { useState, useCallback } from 'react';

export const useGeoJSONImport = () => {
  const [importedLayers, setImportedLayers] = useState([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const validateGeoJSON = (data) => {
    if (!data || typeof data !== 'object') throw new Error('Invalid file format.');
    if (data.type !== 'FeatureCollection' && data.type !== 'Feature') throw new Error('The file must be valid GeoJSON (FeatureCollection or Feature).');
    return true;
  };
  const generateLayerId = () => `imported_layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const detectGeometryType = (data) => {
    if (data.type === 'Feature') return data.geometry?.type || 'Unknown';
    if (data.type === 'FeatureCollection' && data.features?.length) {
      const types = new Set(data.features.map((feature) => feature.geometry?.type).filter(Boolean));
      if (types.size === 1) return Array.from(types)[0];
      if (types.size > 1) return 'Mixed';
    }
    return 'Unknown';
  };
  const getStyleByGeometryType = (geometryType) => {
    const styles = {
      Point: { color: '#ff7800', weight: 2, opacity: 1, fillColor: '#ff7800', fillOpacity: 0.8 },
      MultiPoint: { color: '#ff7800', weight: 2, opacity: 1, fillColor: '#ff7800', fillOpacity: 0.8 },
      LineString: { color: '#3388ff', weight: 3, opacity: 0.8, fillColor: 'transparent', fillOpacity: 0 },
      MultiLineString: { color: '#3388ff', weight: 3, opacity: 0.8, fillColor: 'transparent', fillOpacity: 0 },
      Polygon: { color: '#9b59b6', weight: 2, opacity: 0.8, fillColor: '#9b59b6', fillOpacity: 0.3 },
      MultiPolygon: { color: '#9b59b6', weight: 2, opacity: 0.8, fillColor: '#9b59b6', fillOpacity: 0.3 },
      Mixed: { color: '#34495e', weight: 2, opacity: 0.7, fillColor: '#34495e', fillOpacity: 0.3 },
      Unknown: { color: '#95a5a6', weight: 2, opacity: 0.5, fillColor: '#95a5a6', fillOpacity: 0.2 }
    };
    return styles[geometryType] || styles.Unknown;
  };
  const importGeoJSON = useCallback(async (file) => {
    setImporting(true); setError(null);
    try {
      if (!file.name.endsWith('.geojson') && !file.name.endsWith('.json')) throw new Error('The file must use the .geojson or .json format.');
      const data = JSON.parse(await file.text());
      validateGeoJSON(data);
      const geometryType = detectGeometryType(data);
      const layerId = generateLayerId();
      const newLayer = { id: layerId, layerKey: layerId, name: file.name.replace(/\.(geojson|json)$/i, ''), category: 'imported', active: false, selected: true, data, fileName: file.name, importDate: new Date().toISOString(), featureCount: data.type === 'FeatureCollection' ? data.features.length : 1, geometryType, style: getStyleByGeometryType(geometryType) };
      setImportedLayers((current) => [...current, newLayer]);
      return { success: true, layer: newLayer };
    } catch (importError) {
      const errorMessage = importError.message || 'Error importing file.';
      setError(errorMessage); console.error('Error importing GeoJSON:', importError);
      return { success: false, error: errorMessage };
    } finally { setImporting(false); }
  }, []);
  const importMultipleGeoJSON = useCallback(async (files) => {
    const results = [];
    for (const file of files) results.push(await importGeoJSON(file));
    return results;
  }, [importGeoJSON]);
  const removeImportedLayer = useCallback((layerId) => setImportedLayers((current) => current.filter((layer) => layer.id !== layerId)), []);
  const clearImportedLayers = useCallback(() => { setImportedLayers([]); setError(null); }, []);
  const updateLayerStatus = useCallback((layerId, status) => setImportedLayers((current) => current.map((layer) => layer.id === layerId ? { ...layer, ...status } : layer)), []);
  const getImportStats = useCallback(() => ({ total: importedLayers.length, active: importedLayers.filter((layer) => layer.active).length, selected: importedLayers.filter((layer) => layer.selected).length, totalFeatures: importedLayers.reduce((sum, layer) => sum + layer.featureCount, 0) }), [importedLayers]);
  const getLayerStyle = (layerType) => {
    const styles = { default: { color: '#3388ff', weight: 1, opacity: 0.7, fillColor: '#3388ff', fillOpacity: 0.2 }, MultiPolygon: { color: '#ff4444', weight: 2, opacity: 0.7, fillColor: '#ff4444', fillOpacity: 0.2 }, line: { color: '#00aa00', weight: 3, opacity: 0.8, fillOpacity: 0 } };
    return styles[layerType] || styles.default;
  };
  return { importedLayers, importing, error, importGeoJSON, importMultipleGeoJSON, removeImportedLayer, clearImportedLayers, updateLayerStatus, getImportStats, getLayerStyle };
};
